import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ordersAPI } from '../api/orders';
import Loader from '../components/common/Loader';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8000';
  const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  return `${baseUrl}${normalizedPath}`;
};

const getStatusDisplay = (status) => {
  switch (status) {
    case 'pending':
      return { text: 'Pending', color: 'text-yellow-600' };
    case 'paid':
    case 'processing':
      return { text: 'Processing', color: 'text-gray-600' };
    case 'shipped':
      return { text: 'Shipped', color: 'text-gray-600' };
    case 'delivered':
      return { text: 'Delivered', color: 'text-green-600' };
    case 'cancelled':
      return { text: 'Cancelled', color: 'text-red-500' };
    default:
      return { text: status, color: 'text-gray-500' };
  }
};

const OrderDetail = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const data = await ordersAPI.getById(id);
      setOrder(data);
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;
  if (!order) return (
    <div className="max-w-4xl mx-auto px-6 lg:px-8 py-20 text-center">
      <p className="text-sm text-gray-400">Order not found</p>
      <Link to="/orders" className="inline-block mt-4 text-xs text-gray-900 border-b border-gray-300 hover:border-gray-900 pb-0.5">
        ← Back to Orders
      </Link>
    </div>
  );

  const statusDisplay = getStatusDisplay(order.status);

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
      {/* Back Link */}
      <Link to="/orders" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-900 transition mb-6">
        <ArrowLeftIcon className="h-3 w-3" />
        Back to Orders
      </Link>

      {/* Order Header */}
      <div className="mb-8">
        <h1 className="text-xl lg:text-2xl font-light tracking-tight text-gray-900">
          Order #{order.order_number}
        </h1>
        <div className="flex items-center gap-3 mt-2">
          <p className="text-xs text-gray-400">
            {new Date(order.created_at).toLocaleDateString()}
          </p>
          <span className={`text-[10px] font-medium uppercase tracking-wider ${statusDisplay.color}`}>
            {statusDisplay.text}
          </span>
        </div>
      </div>

      <div className="space-y-8">
        {/* Order Items */}
        <div>
          <h2 className="text-xs font-light tracking-wide uppercase text-gray-400 mb-4">
            Items
          </h2>
          <div className="space-y-4">
            {order.items?.map((item, idx) => {
              const imageUrl = getImageUrl(item.product_image);
              const hasError = imageErrors[idx];
              const itemPrice = item.discount_price || item.unit_price;
              
              return (
                <div key={idx} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0">
                  <div className="w-14 h-16 bg-gray-50 flex-shrink-0 overflow-hidden">
                    {!hasError && imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={item.product_name}
                        className="w-full h-full object-cover"
                        onError={() => setImageErrors(prev => ({ ...prev, [idx]: true }))}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-50">
                        <span className="text-[8px] text-gray-400">No img</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{item.product_name}</p>
                    <p className="text-[10px] text-gray-400 mt-1">
                      Size: {item.product_size} / Color: {item.product_color}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      Qty: {item.quantity}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-900 whitespace-nowrap">
                      ${(itemPrice * item.quantity).toFixed(2)}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      ${itemPrice.toFixed(2)} each
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Shipping & Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Shipping Address */}
          <div>
            <h2 className="text-xs font-light tracking-wide uppercase text-gray-400 mb-3">
              Shipping Address
            </h2>
            <div className="text-sm text-gray-500 leading-relaxed">
              <p>{order.shipping_address}</p>
              <p className="mt-2">Phone: {order.phone_number}</p>
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <h2 className="text-xs font-light tracking-wide uppercase text-gray-400 mb-3">
              Summary
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Subtotal</span>
                <span className="text-gray-900">${order.total_amount?.toFixed(2)}</span>
              </div>
              {order.discount_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Discount</span>
                  <span className="text-red-500">-${order.discount_amount?.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Shipping</span>
                <span className="text-gray-400 text-xs">Calculated at checkout</span>
              </div>
              <div className="border-t border-gray-100 pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-900">Total</span>
                  <span className="text-base font-medium text-gray-900">
                    ${order.final_amount?.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Order Notes */}
        {order.notes && (
          <div>
            <h2 className="text-xs font-light tracking-wide uppercase text-gray-400 mb-3">
              Notes
            </h2>
            <p className="text-sm text-gray-500">{order.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetail;